import { useEffect } from 'react';
import { useAuthStore } from '../stores/authStore';
import { useUserChannelsStore } from '../stores/userChannelsStore';
import { useBoardStore } from '../stores/boardStore';
import { getWebSocketClient } from '../lib/websocket/boardWebSocket';
import { useQueryClient } from '@tanstack/react-query';

/**
 * Global component that handles WebSocket events that should be listened to app-wide,
 * regardless of which page the user is currently on.
 */
export const GlobalSocketManager = () => {
    const queryClient = useQueryClient();
    const token = useAuthStore(state => state.tokens?.accessToken);
    const refreshTokens = useAuthStore(state => state.refreshTokens);
    const isAuthenticated = useAuthStore(state => state.isAuthenticated);
    
    const { updateChannel, setSyncStatusMessage, loadChannels } = useUserChannelsStore();

    useEffect(() => {
        if (!isAuthenticated || !token) return;

        let wsClient: ReturnType<typeof getWebSocketClient> | null = null;

        const getFreshToken = async (): Promise<string> => {
            try {
                const newTokens = await refreshTokens();
                return newTokens.accessToken;
            } catch (error) {
                console.error('[GlobalSocketManager] Failed to refresh token:', error);
                throw error;
            }
        };

        try {
            wsClient = getWebSocketClient(token, getFreshToken);
            wsClient.connect().catch(err => console.error('[GlobalSocketManager] WebSocket connection failed:', err));

            // Handle channel synced event
            const handleChannelSynced = async (data: any) => {
                console.log('[GlobalSocketManager] Channel synced:', data.channelId);
                const status = data.status || 'active';
                
                // Update BOTH stores for maximum reliability
                updateChannel(data.channelId, {
                    status: status,
                    lastSyncAt: data.lastSyncAt
                });
                
                // Update board store if it exists
                try {
                    const boardStore = useBoardStore.getState();
                    if (boardStore.updateChannelStatus) {
                        boardStore.updateChannelStatus(data.channelId, status);
                    }
                } catch (e) {
                    // Board store might not be initialized
                }

                setSyncStatusMessage(data.channelId, "");
                
                // Invalidate relevant queries if we are on a board
                queryClient.invalidateQueries({ queryKey: ['board-payload'] });
            };

            // Handle channel sync status event (transaction/populating phase only).
            // Only update boardStore status — userChannelsStore stays at 'active' so the
            // select-channel modal doesn't flash a loader during data fetching.
            const handleChannelSyncStatus = (data: any) => {
                if (data.channelId && data.message) {
                    try {
                        const boardStore = (useBoardStore.getState as any)();
                        if (boardStore?.updateChannelStatus) {
                            boardStore.updateChannelStatus(data.channelId, 'syncing');
                        }
                    } catch (e) {}

                    setSyncStatusMessage(data.channelId, data.message);
                }
            };

            // Handle channel sync failed event
            const handleChannelSyncFailed = (data: any) => {
                console.log('[GlobalSocketManager] Channel sync failed:', data.channelId);
                const status = data.status || 'error';

                updateChannel(data.channelId, { status });

                try {
                    const boardStore = (useBoardStore.getState as any)();
                    if (boardStore?.updateChannelStatus) {
                        boardStore.updateChannelStatus(data.channelId, status);
                    }
                } catch (e) {}

                setSyncStatusMessage(data.channelId, "");
                queryClient.invalidateQueries({ queryKey: ['board-payload'] });
            };

            // Handle channel entities enrichment complete (background data update)
            const handleChannelEnriched = (_data: { channelId: string }) => {
                // Enrichment complete — refresh board data to pick up images, costs, etc.
                queryClient.invalidateQueries({ queryKey: ['board-payload'] });
            };

            // Handle channel connected — use WS payload directly to avoid
            // a REST loadChannels() race where the response overwrites a
            // WebSocket-driven status update (e.g. 'active' back to 'syncing')
            const handleChannelConnected = (data: any) => {
                if (data.channel) {
                    const existing = useUserChannelsStore.getState().channels.find(
                        (c) => String(c.id) === String(data.channel.id)
                    );
                    if (existing) {
                        updateChannel(data.channel.id, data.channel);
                    } else {
                        useUserChannelsStore.getState().addChannel(data.channel);
                    }
                } else {
                    // Fallback if payload doesn't include channel data
                    loadChannels(true);
                }
                queryClient.invalidateQueries({ queryKey: ['board-payload'] });
            };

            // Handle channel disconnected — full reload needed
            const handleChannelDisconnected = () => {
                loadChannels(true);
                queryClient.invalidateQueries({ queryKey: ['board-payload'] });
            };

            // Handle Shopify billing status change (grace/read_only/suspended/
            // recovered). Refresh the shared billing-status query so the banner
            // and the suspended interstitial react immediately to webhook- and
            // cron-driven changes instead of waiting for the 60s poll. Also
            // dispatch the window event the BillingBanner listens for directly.
            const handleBillingStatusChanged = () => {
                queryClient.invalidateQueries({ queryKey: ['subscriptions', 'billing-status'] });
                if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('billing:status_changed'));
                }
            };

            wsClient.on('channel:synced', handleChannelSynced);
            wsClient.on('channel:sync_status', handleChannelSyncStatus);
            wsClient.on('channel:sync_failed', handleChannelSyncFailed);
            wsClient.on('channel:enriched', handleChannelEnriched);
            wsClient.on('channel:connected', handleChannelConnected);
            wsClient.on('channel:disconnected', handleChannelDisconnected);
            wsClient.on('billing:status_changed', handleBillingStatusChanged);

            return () => {
                if (wsClient) {
                    wsClient.off('channel:synced', handleChannelSynced);
                    wsClient.off('channel:sync_status', handleChannelSyncStatus);
                    wsClient.off('channel:sync_failed', handleChannelSyncFailed);
                    wsClient.off('channel:enriched', handleChannelEnriched);
                    wsClient.off('channel:connected', handleChannelConnected);
                    wsClient.off('channel:disconnected', handleChannelDisconnected);
                    wsClient.off('billing:status_changed', handleBillingStatusChanged);
                }
            };
        } catch (error) {
            console.error('[GlobalSocketManager] Initialization error:', error);
        }
    }, [isAuthenticated, token, refreshTokens, updateChannel, setSyncStatusMessage, loadChannels, queryClient]);

    return null;
};
